import escapeRegExp from "lodash/escapeRegExp"
import PropTypes from "prop-types"
import React, { useState } from "react"

import Button from "src/components/Button"
import InlineNotice from "src/components/InlineNotice"
import Modal from "src/components/Modal"

const emphasize = (text, terms) => {
  if (!terms?.length) return text

  const pattern = new RegExp(`(${terms.map(escapeRegExp).join("|")})`)
  return text
    .split(pattern)
    .map((part, index) =>
      terms.includes(part) ? <strong key={index}>{part}</strong> : part
    )
}

const impactShape = PropTypes.shape({
  heading: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      detail: PropTypes.string.isRequired,
    })
  ).isRequired,
})

const ConfirmImpact = ({ impact, emphasis }) => (
  <>
    <p className="mb-1">{emphasize(impact.heading, emphasis)}</p>
    <ul className="mb-0 list-disc pl-5">
      {impact.items.map((item) => (
        <li key={item.label}>
          <strong>{item.label}</strong>: {item.detail}
        </li>
      ))}
    </ul>
  </>
)

ConfirmImpact.propTypes = {
  impact: impactShape.isRequired,
  emphasis: PropTypes.arrayOf(PropTypes.string),
}

const ContactLinkIssueNotice = ({
  contactEmail,
  accountEmail,
  message,
  contactEmailLabel,
  accountEmailLabel,
  fixButtonLabel,
  fixFormId,
  confirmTitle,
  confirmMessage,
  cancelLabel,
  keepButtonLabel,
  keepFormId,
  keepConfirmTitle,
  keepConfirm,
  archivedMessage,
}) => {
  const [confirming, setConfirming] = useState(null)
  const closeConfirmation = () => setConfirming(null)

  return (
    <InlineNotice
      variant="warning"
      icon="fa-triangle-exclamation"
      iconStyle="solid"
    >
      <p className="mb-2">{message}</p>
      <dl className="mb-0 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
        <dt className="font-semibold">{accountEmailLabel}</dt>
        <dd className="mb-0 break-all">{accountEmail}</dd>
        <dt className="font-semibold">{contactEmailLabel}</dt>
        <dd className="mb-0 break-all">{contactEmail}</dd>
      </dl>
      {(fixFormId || keepFormId) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {fixFormId && (
            <Button
              type="button"
              variant="secondary"
              small
              onClick={() => setConfirming("account")}
            >
              {fixButtonLabel}
            </Button>
          )}
          {keepFormId && (
            <Button
              type="button"
              variant="secondary"
              small
              onClick={() => setConfirming("customer")}
            >
              {keepButtonLabel}
            </Button>
          )}
        </div>
      )}
      {archivedMessage && <p className="mb-0 mt-3">{archivedMessage}</p>}
      {confirming === "account" && (
        <Modal isOpen onClose={closeConfirmation} size="small" stacked>
          <Modal.Header title={confirmTitle} />
          <Modal.Body>
            <p>{confirmMessage}</p>
          </Modal.Body>
          <Modal.Footer
            onClose={closeConfirmation}
            cancelBtnText={cancelLabel}
            confirmBtnText={fixButtonLabel}
            confirmBtnType="submit"
            confirmBtnForm={fixFormId}
          />
        </Modal>
      )}
      {confirming === "customer" && (
        <Modal isOpen onClose={closeConfirmation} size="small" stacked>
          <Modal.Header title={keepConfirmTitle} />
          <Modal.Body>
            {keepConfirm?.paragraphs.map((paragraph) => (
              <p key={paragraph}>
                {emphasize(paragraph, keepConfirm.emphasis)}
              </p>
            ))}
            {keepConfirm?.impact && (
              <ConfirmImpact
                impact={keepConfirm.impact}
                emphasis={keepConfirm.emphasis}
              />
            )}
          </Modal.Body>
          <Modal.Footer
            onClose={closeConfirmation}
            cancelBtnText={cancelLabel}
            confirmBtnText={keepButtonLabel}
            confirmBtnType="submit"
            confirmBtnForm={keepFormId}
            confirmBtnVariant="danger"
          />
        </Modal>
      )}
    </InlineNotice>
  )
}

ContactLinkIssueNotice.propTypes = {
  contactEmail: PropTypes.string.isRequired,
  accountEmail: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  contactEmailLabel: PropTypes.string.isRequired,
  accountEmailLabel: PropTypes.string.isRequired,
  fixButtonLabel: PropTypes.string.isRequired,
  fixFormId: PropTypes.string,
  confirmTitle: PropTypes.string,
  confirmMessage: PropTypes.string,
  cancelLabel: PropTypes.string,
  keepButtonLabel: PropTypes.string,
  keepFormId: PropTypes.string,
  keepConfirmTitle: PropTypes.string,
  keepConfirm: PropTypes.shape({
    paragraphs: PropTypes.arrayOf(PropTypes.string).isRequired,
    emphasis: PropTypes.arrayOf(PropTypes.string),
    impact: impactShape,
  }),
  archivedMessage: PropTypes.string,
}

export default ContactLinkIssueNotice
